import { validate } from "email-validator";
import React, { useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import { copy } from "shared/assets";
import { FieldWrapper } from "../../FieldWrapper";
import { SelectProps } from "../Select";
import { StyledCreatableInputSelect } from "../styles";
import { ISelectOptionBase } from "../types";
import { DefaultSelectValue } from "./DefaultValue";

interface Props extends SelectProps {
  isAsync?: boolean;
}

export const MultiCreatableSelect: React.FC<Props> = ({
  name,
  label,
  required,
  disabled,
  placeholder,
  style,
  SelectValue = DefaultSelectValue,
  prefixIcon,
  error,
  validation,
  options = [],
  readOnly,
  isSearchable = false,
  isCreatable = false,
  isMulti = false,
  onChange: handleChange,
  onDropdownClose,
  control,
  ...props
}) => {
  const [inputValue, setInputValue] = useState("");
  const [addedValues, setAddedValues] = useState<ISelectOptionBase[]>([]);

  const handleKeyDown = (event: any, onChange: any) => {
    if (!inputValue) return;

    switch (event.key) {
      case "Enter":
      case "Tab":
      case " ":
        event.preventDefault();

        persistInput(onChange);
    }
  };

  const persistInput = (onChange: any) => {
    if (!validate(inputValue) || isInList) return;

    const updatedAddedValues = [
      ...addedValues,
      { label: inputValue, value: inputValue },
    ];

    setAddedValues(updatedAddedValues);
    setInputValue("");

    onChange(
      updatedAddedValues.length > 0
        ? updatedAddedValues.map(({ value }: ISelectOptionBase) => value)
        : undefined
    );
    handleChange && handleChange(updatedAddedValues);
  };

  const isInList = useMemo(
    () => !!addedValues.find(({ value }) => value === inputValue),
    [inputValue, addedValues]
  );

  const note = useMemo(() => {
    if (isInList) {
      return copy.event.email.messages.alreadyInEmailList.replace(
        "%e",
        inputValue
      );
    }

    if (validate(inputValue)) {
      return copy.event.email.messages.enterToAdd.replace("%e", inputValue);
    }

    return undefined;
  }, [isInList, inputValue]);

  return (
    <FieldWrapper
      name={name}
      label={label}
      required={required}
      style={style}
      error={error}
      note={note || props.note}
    >
      <Controller
        name={name}
        control={control}
        rules={validation}
        render={({ onChange }) => (
          <StyledCreatableInputSelect
            {...props}
            id={name}
            value={addedValues}
            isDisabled={disabled}
            components={{
              DropdownIndicator: null,
            }}
            inputValue={inputValue}
            isClearable
            isMulti
            menuIsOpen={false}
            classNamePrefix={"multi-creatable"}
            onBlur={() => persistInput(onChange)}
            onChange={(newValue: any | ISelectOptionBase) => {
              setAddedValues(newValue);

              onChange(
                newValue.length > 0
                  ? newValue.map(({ value }: ISelectOptionBase) => value)
                  : undefined
              );
              handleChange && handleChange(newValue);
            }}
            onInputChange={(newValue) => setInputValue(newValue)}
            onKeyDown={(event) => handleKeyDown(event, onChange)}
            placeholder={placeholder}
          />
        )}
      />
    </FieldWrapper>
  );
};
